import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Alert, Box, Button, Typography } from "@mui/material";
import GoogleIcon from "@mui/icons-material/Google";
import { useAuth } from "../../common/auth";
import { C } from "../../common/theme";
import { ROUTES } from "../../common/constant/routes";

/** Sign-in: a centred card on the warm ground. Sits outside the app layout,
 * so errors show in its own Alert (there is no toast here). */
export default function Login() {
  const { signIn, adminUser, loading, error, firebaseUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [hasAttemptedLogin, setHasAttemptedLogin] = React.useState(false);

  const locationError = (location.state as any)?.error;

  // If already authenticated, redirect to dashboard
  useEffect(() => {
    if (adminUser && !loading) {
      navigate(ROUTES.LANDING, { replace: true });
    }
  }, [adminUser, loading, navigate]);

  const handleSignIn = async () => {
    setHasAttemptedLogin(true);
    await signIn();
  };

  const checking = loading && !!firebaseUser;

  return (
    <Box
      component="main"
      sx={{
        minHeight: "100dvh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        bgcolor: C.ground,
        px: 2,
        py: 4,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          width: "100%",
          maxWidth: 400,
          bgcolor: C.paper,
          border: `1px solid ${C.line}`,
          borderRadius: "12px",
          p: { xs: 3, sm: 4 },
          boxSizing: "border-box",
          textAlign: "center",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1,
        }}
      >
        <Box
          sx={{
            width: 64,
            height: 64,
            borderRadius: "14px",
            bgcolor: C.paper,
            border: `1px solid ${C.line}`,
            display: "grid",
            placeItems: "center",
            mb: 1,
          }}
        >
          <Box component="img" src="/logo.png" alt="Inflos" sx={{ width: 40, height: 40, objectFit: "contain" }} />
        </Box>
        <Typography variant="h4" component="h1">
          Inflos Admin
        </Typography>
        <Typography variant="body2" sx={{ color: C.muted, mb: 2 }}>
          Sign in to manage the Inflos platform
        </Typography>

        {hasAttemptedLogin && (error || locationError) && (
          <Alert severity="error" sx={{ width: "100%", boxSizing: "border-box", textAlign: "left", mb: 1 }}>
            {error || locationError}
          </Alert>
        )}

        <Button
          variant="contained"
          color="primary"
          size="large"
          fullWidth
          startIcon={<GoogleIcon />}
          onClick={handleSignIn}
          disabled={loading}
          sx={{ minHeight: 48, fontSize: "1rem", fontWeight: 600, textTransform: "none" }}
        >
          {checking ? "Checking your access…" : "Continue with Google"}
        </Button>

        <Typography variant="caption" sx={{ mt: 2, color: C.muted }}>
          Only authorised admin accounts can access this dashboard. Contact a superadmin if you need access.
        </Typography>
      </Box>
    </Box>
  );
}
